import axios, { AxiosResponse } from 'axios';
import { router } from '../router/Routes';
import { toast } from 'react-toastify';
import { LoginFormValues, ReportFormValues, User, UserBillingAddressFormValues, UserForgotPasswordValues, UserFormValues, UserShippingAddressFormValues } from '../models/user';
import { RouteEnvelope, RouteTerminalEnvelope, Terminal, TerminalEnvelope } from '../models/form';
import { Permit, Rider, UserPermitEnvelope, Vehicle, Workdest } from '../models/permit';
import { store } from '../stores/store';
import { PaginatedResult } from '../models/pagination';

// const sleep = (delay: number) => {
//     return new Promise((resolve) => {
//         setTimeout(resolve, delay)
//     })
// }

axios.defaults.baseURL = process.env.REACT_APP_API_URL;

axios.interceptors.request.use(config => {
    const token = store.commonStore.token;
    if (token) config.headers!.Authorization = `Bearer ${token}`
    return config;
})

axios.interceptors.response.use(async response => {
    // if (process.env.NODE_ENV === 'development') await sleep(1000);
    const pagination = response.headers['pagination'];
    if (pagination) {
        response.data = new PaginatedResult(response.data, JSON.parse(pagination));
        return response as AxiosResponse<PaginatedResult<any>>
    }
    return response;
}, (error) => {
    const { data, status, config, headers } = error.response!;
    switch (status) {
        case 400:
            if (config.method === 'get' && data.errors.hasOwnProperty('id')) {
                router.navigate(`${process.env.PUBLIC_URL}/not-found`);
            }
            if (data.errors) {
                const modalStateErrors = [];
                for (const key in data.errors) {
                    if (data.errors[key]) {
                        modalStateErrors.push(data.errors[key])
                    }
                }
                throw modalStateErrors.flat();
            } else {
                toast.error(data);
            }
            break;
        case 401:
            if (status === 401 && headers['www-authenticate']?.startsWith('Bearer error="invalid_token"')) {
                store.userStore.logout();
            }
            break;
        case 403:
            router.navigate(`${process.env.PUBLIC_URL}/not-found`);
            break;
        case 404:
            router.navigate(`${process.env.PUBLIC_URL}`);
            break;
        case 500:
            store.commonStore.setServerError(data);
            router.navigate(`${process.env.PUBLIC_URL}/server-error`);
            toast.error('Server error - please come back later');
            break;
    }
    return Promise.reject(error);
})

const responseBody = <T>(response: AxiosResponse<T>) => response.data;

const requests = {
    get: <T>(url: string) => axios.get<T>(url).then(responseBody),
    post: <T>(url: string, body: {}) => axios.post<T>(url, body).then(responseBody),
    put: <T>(url: string, body: {}) => axios.put<T>(url, body).then(responseBody),
    del: <T>(url: string) => axios.delete<T>(url).then(responseBody),
}

const Permits = {
    list: () =>
        requests.get<UserPermitEnvelope>(`/permits`),
    filterList: (predicate: string) =>
        requests.get<UserPermitEnvelope>(`/permits/${predicate}/permits`),
    create: (permit: Permit) => requests.post<void>('/permits/', permit),
    updateWorkdest: (workdest: Workdest, permitId: number) => requests.put<void>(`/permits/${permitId}/workdest`, workdest),
    updateVehicles: (vehicles: Vehicle[], permitId: number) => requests.put<void>(`/permits/${permitId}/vehicles`, {vehicles}),
    updateTempRider: (riders: Rider[], permitId: number) => requests.put<void>(`/permits/${permitId}/riders`, {riders}),
    loadRoutes: () => requests.get<RouteEnvelope>('/form/routes'),
    loadTerminals: () => requests.get<Terminal[]>('/form/terminals'),
    loadRouteTerminals: (routeId: number) => requests.get<TerminalEnvelope>(`/form/route_terminals/${routeId}`),
    loadRouteScheduleTimes: (routeId: number, amTerminal: number) => 
        requests.get<RouteTerminalEnvelope>(`/form/route_schedule_times/routeId=${routeId}&amTerminal=${amTerminal}`),
    details: (id: string) => requests.get<Permit>(`/permits/${id}`),
    delete: (id: string) => requests.del(`/permits/${id}`),
};

const Account = {
    current: () => requests.get<User>('/account'),
    login: (user: LoginFormValues) => requests.post<User>(`/account/login`, user),
    register: (user: UserFormValues) => requests.post<User>(`/account/register`, user),
    updateShipping: (user: UserShippingAddressFormValues) => requests.put<User>(`/account/updateShipping`, user),
    setShipping: () => requests.put(`/account/setShipping`, {}),
    updateBilling: (user: UserBillingAddressFormValues) => requests.put<User>(`/account/updateBilling`, user),
    setBilling: () => requests.put(`/account/setBilling`, {}),
    forgotPassword: (user: UserForgotPasswordValues) => requests.post(`/account/forgotPassword`, user),
    reportIssue: (user: ReportFormValues) => requests.post<boolean>(`/account/reportIssue`, user),
};

const Emails = {
    send: (subject: string, message: string) => requests.get(`/mail/error?subject=${subject}&message=${message}`),
};
  
const agent = {
    Account,
    Permits,
    Emails
};

export default agent;